import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Gauge, ShieldCheck, Flask, ShareNetwork, CalendarBlank, ChartLineUp,
  SlidersHorizontal, SignOut, Sparkle, ArrowsClockwise, Article, Brain,
  NotePencil, EnvelopeSimple, Rocket, XCircle, PaperPlaneTilt,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: Gauge, end: true },
  { to: "/validation", label: "File de validation", icon: ShieldCheck },
  { to: "/publication", label: "Publication", icon: Rocket },
  { to: "/brouillons", label: "Brouillons", icon: NotePencil },
  { to: "/contenus", label: "Bibliothèque", icon: Article },
  { to: "/newsletter", label: "Newsletter", icon: PaperPlaneTilt },
  { to: "/rejets", label: "Sujets rejetés", icon: XCircle },
  { to: "/veille", label: "Veille scientifique", icon: Flask },
  { to: "/calendrier", label: "Calendrier éditorial", icon: CalendarBlank },
  { to: "/maillage", label: "Maillage interne", icon: ShareNetwork },
  { to: "/performance", label: "Performance", icon: ChartLineUp },
  { to: "/emails", label: "Historique e-mails", icon: EnvelopeSimple },
  { to: "/strategie", label: "Stratégie", icon: SlidersHorizontal },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [running, setRunning] = useState([]);

  const poll = async () => {
    try {
      const { data } = await api.get("/agents/runs");
      setRunning(data.filter((r) => r.status === "running"));
    } catch (e) {}
  };
  useEffect(() => { poll(); const t = setInterval(poll, 5000); return () => clearInterval(t); }, []);

  // Register the current app URL so e-mail links & feed URLs auto-target this deployment.
  useEffect(() => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (url) api.post("/config/base", { url }).catch(() => {});
  }, []);

  const runCycle = async () => {
    try {
      await api.post("/agents/daily_cycle/run");
      toast.success("Cycle autonome lancé", { description: "Veille · analyse · génération · maillage" });
      poll();
    } catch (e) { toast.error("Impossible de lancer le cycle"); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-bone">
      {/* Sidebar */}
      <aside className="w-[264px] shrink-0 bg-white border-r border-border flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2 text-brand">
            <Sparkle weight="fill" size={18} />
            <span className="font-serif text-xl tracking-tight">Valorian</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mt-1">Autonomous SEO · GEO Engine</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-brand-soft text-brand font-medium" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
              <n.icon size={18} weight="regular" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-xs font-medium">
              {(user?.name || "V").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="text-muted-foreground hover:text-red-600 transition-colors">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-[264px]">
        <header className="sticky top-0 z-20 bg-white border-b border-border px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm">
            {running.length > 0 ? (
              <span className="flex items-center gap-2 text-ai font-mono text-xs tracking-wide">
                <Brain size={16} weight="fill" className="ai-pulse" />
                {running.length} agent{running.length > 1 ? "s" : ""} en cours · {running.map((r) => r.agent).join(", ")}
              </span>
            ) : (
              <span className="flex items-center gap-2 text-muted-foreground font-mono text-xs tracking-wide">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Moteur au repos
              </span>
            )}
          </div>
          <button data-testid="run-cycle-btn" onClick={runCycle}
            className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
            <ArrowsClockwise size={16} /> Lancer le cycle autonome
          </button>
        </header>
        <main className="p-8 max-w-[1400px]">
          <Outlet context={{ refreshRunning: poll }} />
        </main>
      </div>
    </div>
  );
}
